(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const enhancePreviewLink = (link) => {
    const current = new URL(window.location.href);
    const previewThemeId = current.searchParams.get('preview_theme_id');
    if (!previewThemeId) return;
    const destination = new URL(link.href, window.location.origin);
    destination.searchParams.set('preview_theme_id', previewThemeId);
    link.href = destination.toString();
  };

  for (const link of document.querySelectorAll('[data-amour-review-page-link]')) {
    enhancePreviewLink(link);
  }

  const replaceFormFieldLabel = (shadow, shell) => {
    const orderNumberLabel = shell.dataset.amourOrderNumberLabel;
    if (!orderNumberLabel) return;
    const sourceLabel = 'Product purchased';
    const walker = document.createTreeWalker(shadow, NodeFilter.SHOW_TEXT);
    let textNode = walker.nextNode();
    while (textNode) {
      if (textNode.textContent.trim().toLowerCase() === sourceLabel.toLowerCase()) {
        textNode.textContent = textNode.textContent.replace(/Product purchased/i, orderNumberLabel);
      }
      textNode = walker.nextNode();
    }
    shadow.querySelectorAll('[aria-label]').forEach((element) => {
      if (element.getAttribute('aria-label')?.trim().toLowerCase() === sourceLabel.toLowerCase()) {
        element.setAttribute('aria-label', orderNumberLabel);
      }
    });
  };

  const showReviewSuccess = (shell) => {
    const toast = shell.querySelector('[data-amour-review-success]');
    if (!toast || toast.dataset.amourVisible === 'true') return;
    const closeButton = toast.querySelector('[data-amour-review-success-close]');
    const previousFocus = document.activeElement;
    const message = shell.dataset.amourSuccessMessage;
    const text = toast.querySelector('[data-amour-review-success-text]');
    if (text) text.textContent = message;
    toast.hidden = false;
    toast.dataset.amourVisible = 'true';

    const close = () => {
      toast.hidden = true;
      toast.dataset.amourVisible = 'false';
      document.removeEventListener('keydown', onEscape);
      if (previousFocus instanceof HTMLElement) previousFocus.focus({preventScroll: true});
    };
    const onEscape = (event) => {
      if (event.key !== 'Escape') return;
      close();
    };
    closeButton?.addEventListener('click', close, {once: true});
    document.addEventListener('keydown', onEscape);
    window.requestAnimationFrame(() => closeButton?.focus({preventScroll: true}));
  };

  const enhanceReviewForm = (embed, shell) => {
    const shadow = embed.shadowRoot;
    if (!shadow) return false;
    replaceFormFieldLabel(shadow, shell);
    const form = shadow.querySelector('form, [data-testid="form"]');
    if (form) {
      embed.dataset.amourHadForm = 'true';
      if (form.dataset.amourReviewSubmitWatcher !== 'true') {
        form.dataset.amourReviewSubmitWatcher = 'true';
        form.addEventListener('submit', () => {
          embed.dataset.amourSubmissionPending = 'true';
        });
      }
    } else if (
      embed.dataset.amourHadForm === 'true'
      && embed.dataset.amourSubmissionPending === 'true'
      && shadow.textContent.trim()
    ) {
      embed.dataset.amourSubmissionPending = 'false';
      showReviewSuccess(shell);
    }

    if (!shadow.querySelector('[data-amour-review-form-style]')) {
      const style = document.createElement('style');
      style.dataset.amourReviewFormStyle = 'true';
      style.textContent = `
      [data-form-root] section:has(> h2) { display: none; }
      [data-form-root] form { gap: 16px; }
      fieldset[name="custom#rating"] { margin: 2px 0 4px; }
      fieldset[name="custom#rating"] > div {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 8px;
      }
      fieldset[name="custom#rating"] label {
        position: relative;
        min-height: 48px;
        justify-content: center;
        padding: 6px 2px;
        border: 0;
        background: transparent;
        text-align: center;
        cursor: pointer;
      }
      fieldset[name="custom#rating"] label span { font-size: 0; }
      fieldset[name="custom#rating"] label span::after {
        content: '☆';
        color: #6f685f;
        font-size: 32px;
        line-height: 1;
      }
      fieldset[name="custom#rating"] label:has(~ label input:checked) span::after,
      fieldset[name="custom#rating"] label:has(input:checked) span::after {
        content: '★';
        color: #c39a5b;
      }
      fieldset[name="custom#rating"] label:has(input:focus-visible) {
        outline: 2px solid #a8844e;
        outline-offset: 2px;
      }
      fieldset[name="custom#rating"] input {
        position: absolute;
        width: 1px;
        height: 1px;
        overflow: hidden;
        opacity: 0;
      }
      [data-testid="btn-form-submit"] {
        min-height: 54px;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }
      @media (min-width: 750px) {
        [data-testid="form"] {
          display: grid !important;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          column-gap: 20px;
        }
        [data-testid="form"] > * { grid-column: 1 / -1 !important; }
        [data-testid="form"] > div:has(> #first_name) { grid-column: 1 !important; }
        [data-testid="form"] > div:has(> #email) { grid-column: 2 !important; }
      }
      @media (prefers-reduced-motion: reduce) {
        fieldset[name="custom#rating"] label { transition: none; }
      }
      `;
      shadow.append(style);
    }
    return true;
  };

  const initReviewForms = () => {
    const formShells = document.querySelectorAll('[data-amour-review-form-shell]');
    formShells.forEach((shell) => {
      if (shell.dataset.amourReviewFormWatcher === 'true') return;
      shell.dataset.amourReviewFormWatcher = 'true';

      const bindEmbed = () => {
        const embed = shell.querySelector('shopify-forms-embed');
        if (!embed || embed.dataset.amourReviewFormWatcher === 'true') return;
        embed.dataset.amourReviewFormWatcher = 'true';
        let observedShadow;
        let shadowObserver;
        const applyEnhancement = () => {
          const shadow = embed.shadowRoot;
          if (!shadow) return false;
          enhanceReviewForm(embed, shell);
          if (shadow !== observedShadow) {
            shadowObserver?.disconnect();
            observedShadow = shadow;
            shadowObserver = new MutationObserver(() => enhanceReviewForm(embed, shell));
            shadowObserver.observe(shadow, {childList: true, subtree: true});
          }
          return true;
        };

        if (applyEnhancement()) return;
        let attempts = 0;
        const timer = window.setInterval(() => {
          attempts += 1;
          if (!applyEnhancement() && attempts < 40) return;
          window.clearInterval(timer);
        }, 250);
      };

      new MutationObserver(bindEmbed).observe(shell, {childList: true, subtree: true});
      bindEmbed();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReviewForms, {once: true});
  } else {
    initReviewForms();
  }

  for (const trigger of document.querySelectorAll('[data-amour-write-review]')) {
    if (trigger.dataset.amourWriteReviewReady === 'true') continue;
    trigger.dataset.amourWriteReviewReady = 'true';
    trigger.addEventListener('click', (event) => {
      const form = document.querySelector('#amour-review-form');
      if (!form) return;
      event.preventDefault();
      window.history.replaceState(null, '', '#amour-review-form');
      form.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
      form.focus({preventScroll: true});
    });
  }

  for (const root of document.querySelectorAll('[data-amour-reviews]:not([data-amour-ready])')) {
    root.dataset.amourReady = 'true';
    const cards = Array.from(root.querySelectorAll('[data-amour-review-card]'));
    const grid = root.querySelector('[data-amour-review-grid]');
    const empty = root.querySelector('[data-amour-empty]');
    const pagination = root.querySelector('[data-amour-pagination]');
    const reveal = root.querySelector('[data-amour-reveal]');
    const revealButton = root.querySelector('[data-amour-reveal-button]');
    const revealStatus = root.querySelector('[data-amour-reveal-status]');
    const sort = root.querySelector('[data-amour-sort]');
    const ratingFilter = root.querySelector('[data-amour-rating-filter]');
    const filters = Array.from(root.querySelectorAll('[data-amour-filter]'));
    const pageSize = Math.max(1, Number(root.dataset.pageSize || 20));
    const progressiveReveal = root.dataset.progressiveReveal === 'true';
    const continuousReveal = root.dataset.continuousReveal === 'true';
    const initialSize = Math.max(1, Number(root.dataset.initialSize || 8));
    const expandedSize = Math.max(initialSize, Number(root.dataset.expandedSize || 20));
    const revealBatchSize = Math.max(1, Number(root.dataset.revealBatchSize || (expandedSize - initialSize) || 12));
    const state = {filter: 'all', rating: '', page: 1, sort: 'manual', visibleLimit: initialSize};

    const orderMedia = (container) => {
      if (!container) return;
      Array.from(container.querySelectorAll(':scope > [data-amour-ordered-media]'))
        .sort((a, b) => {
          const orderDifference = Number(a.dataset.displayOrder) - Number(b.dataset.displayOrder);
          if (orderDifference) return orderDifference;
          const sourceDifference = Number(a.dataset.sourceIndex) - Number(b.dataset.sourceIndex);
          if (sourceDifference) return sourceDifference;
          return Number(a.dataset.mediaIndex) - Number(b.dataset.mediaIndex);
        })
        .forEach((item) => container.append(item));
    };

    orderMedia(root.querySelector('.amour-reviews__hero-mosaic'));
    orderMedia(root.querySelector('[data-amour-story-rail]'));

    let masonryFrame;
    const layoutMasonry = () => {
      if (!grid) return;
      window.cancelAnimationFrame(masonryFrame);
      masonryFrame = window.requestAnimationFrame(() => {
        masonryFrame = window.requestAnimationFrame(() => {
          const styles = window.getComputedStyle(grid);
          const rowHeight = Number.parseFloat(styles.gridAutoRows);
          const rowGap = Number.parseFloat(styles.rowGap);
          if (!Number.isFinite(rowHeight) || rowHeight <= 0) return;
          grid.querySelectorAll('[data-amour-review-card]:not([hidden])').forEach((card) => {
            card.style.gridRowEnd = 'auto';
            const cardHeight = card.getBoundingClientRect().height;
            const span = Math.ceil((cardHeight + rowGap) / (rowHeight + rowGap));
            card.style.gridRowEnd = `span ${Math.max(1, span)}`;
          });
        });
      });
    };

    const filteredCards = () => cards
      .filter((card) => {
        if (state.filter === 'photos') return card.dataset.hasPhotos === 'true';
        return true;
      })
      .filter((card) => !state.rating || card.dataset.rating === state.rating)
      .sort((a, b) => {
        if (state.sort === 'highest') return Number(b.dataset.rating) - Number(a.dataset.rating);
        if (state.sort === 'lowest') return Number(a.dataset.rating) - Number(b.dataset.rating);
        if (state.sort === 'manual') {
          const orderDifference = Number(a.dataset.displayOrder) - Number(b.dataset.displayOrder);
          if (orderDifference) return orderDifference;
          return Number(a.dataset.sourceIndex) - Number(b.dataset.sourceIndex);
        }
        return new Date(b.dataset.date || 0) - new Date(a.dataset.date || 0);
      });

    const createPaginationButton = (label, disabled, page) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      button.disabled = disabled;
      button.addEventListener('click', () => {
        state.page = page;
        render();
        root.querySelector('[data-amour-controls]')?.scrollIntoView({
          behavior: reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start',
        });
      });
      return button;
    };

    const render = () => {
      if (!grid || !empty) return;
      const visible = filteredCards();
      const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
      state.page = Math.min(state.page, pageCount);
      const start = (state.page - 1) * pageSize;
      cards.forEach((card) => { card.hidden = true; });
      visible.forEach((card) => grid.append(card));
      if (progressiveReveal) {
        const availableLimit = continuousReveal ? visible.length : Math.min(visible.length, expandedSize);
        visible.slice(0, Math.min(state.visibleLimit, availableLimit)).forEach((card) => { card.hidden = false; });
      } else {
        visible.slice(start, start + pageSize).forEach((card) => { card.hidden = false; });
      }
      empty.hidden = visible.length !== 0;
      if (progressiveReveal && reveal && revealButton) {
        const availableLimit = continuousReveal ? visible.length : Math.min(visible.length, expandedSize);
        const expanded = state.visibleLimit >= availableLimit;
        reveal.hidden = visible.length <= initialSize;
        revealButton.disabled = expanded;
        revealButton.setAttribute('aria-expanded', String(continuousReveal ? state.visibleLimit > initialSize : expanded));
        revealButton.textContent = expanded
          ? (root.dataset.reviewsShownLabel || '__COUNT__ reviews shown').replace('__COUNT__', String(availableLimit))
          : (root.dataset.seeMoreLabel || 'See more reviews');
      } else if (pagination) {
        pagination.replaceChildren();
        if (pageCount > 1) {
          pagination.append(
            createPaginationButton(root.dataset.previousLabel, state.page === 1, state.page - 1),
            Object.assign(document.createElement('span'), {
              textContent: `${root.dataset.pageLabel} ${state.page} / ${pageCount}`,
            }),
            createPaginationButton(root.dataset.nextLabel, state.page === pageCount, state.page + 1),
          );
        }
      }
      layoutMasonry();
    };

    const selectFilter = (value) => {
      state.filter = value;
      state.page = 1;
      state.visibleLimit = initialSize;
      if (revealStatus) revealStatus.textContent = '';
      filters.forEach((filter) => {
        const selected = filter.dataset.amourFilter === value;
        filter.classList.toggle('is-active', selected);
        filter.setAttribute('aria-pressed', String(selected));
      });
      render();
    };

    filters.forEach((filter) => {
      filter.addEventListener('click', () => selectFilter(filter.dataset.amourFilter || 'all'));
    });

    root.querySelector('[data-amour-photo-filter]')?.addEventListener('click', () => {
      selectFilter('photos');
      root.querySelector('[data-amour-controls]')?.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    });

    sort?.addEventListener('change', () => {
      state.sort = sort.value;
      state.page = 1;
      state.visibleLimit = initialSize;
      if (revealStatus) revealStatus.textContent = '';
      render();
    });

    ratingFilter?.addEventListener('change', () => {
      state.rating = ratingFilter.value;
      state.page = 1;
      state.visibleLimit = initialSize;
      if (revealStatus) revealStatus.textContent = '';
      render();
    });

    const revealReviews = () => {
      const matchingCount = filteredCards().length;
      const availableLimit = continuousReveal ? matchingCount : Math.min(matchingCount, expandedSize);
      if (state.visibleLimit >= availableLimit) return;
      state.visibleLimit = continuousReveal
        ? Math.min(availableLimit, state.visibleLimit + revealBatchSize)
        : expandedSize;
      render();
      if (revealStatus) {
        const visibleCount = Math.min(matchingCount, state.visibleLimit, availableLimit);
        revealStatus.textContent = (root.dataset.reviewsShownLabel || '__COUNT__ reviews shown')
          .replace('__COUNT__', String(visibleCount));
      }
    };

    revealButton?.addEventListener('click', revealReviews);
    revealButton?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      revealReviews();
    });

    grid?.addEventListener('load', (event) => {
      if (event.target instanceof HTMLImageElement) layoutMasonry();
    }, true);
    window.addEventListener('resize', layoutMasonry, {passive: true});
    document.fonts?.ready.then(layoutMasonry);

    for (const trigger of root.querySelectorAll('[data-amour-lightbox]')) {
      trigger.addEventListener('click', () => {
        const source = trigger.querySelector('img');
        if (!source) return;
        const reviewSource = trigger.closest('[data-amour-review-source]');
        const reviewTemplate = reviewSource?.querySelector('[data-amour-lightbox-review]');
        const dialog = document.createElement('dialog');
        dialog.className = 'amour-reviews__lightbox';
        dialog.setAttribute('aria-label', source.alt || root.dataset.closeLabel || 'Customer review');
        const close = document.createElement('button');
        close.type = 'button';
        close.className = 'amour-reviews__lightbox-close';
        close.setAttribute('aria-label', root.dataset.closeLabel || 'Close');
        const closeIcon = document.createElement('i');
        closeIcon.className = 'fa fa-times';
        closeIcon.setAttribute('aria-hidden', 'true');
        close.append(closeIcon);
        const image = source.cloneNode();
        image.removeAttribute('loading');
        const panel = document.createElement('div');
        panel.className = 'amour-reviews__lightbox-panel';
        const media = document.createElement('div');
        media.className = 'amour-reviews__lightbox-media';
        media.append(image);
        panel.append(media);
        if (reviewTemplate instanceof HTMLTemplateElement) {
          panel.append(reviewTemplate.content.cloneNode(true));
        }
        dialog.append(close, panel);
        close.addEventListener('click', () => dialog.close());
        dialog.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          dialog.close();
        });
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => {
          dialog.remove();
          trigger.focus({preventScroll: true});
        }, {once: true});
        document.body.append(dialog);
        dialog.showModal();
        close.focus();
      });
    }

    render();
  }
})();
